'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import { Link } from '@/i18n/navigation';
import { CaretLeft, CaretRight, Heart, House, Info, Package } from '@phosphor-icons/react';
import { useRouter, usePathname } from 'next/navigation';
import AddToCartButton from '@/components/storefront/AddToCartButton';
import type { ListingCard } from '@/lib/brand/types';
import ProductGalleryCarousel from '@/components/storefront/ProductGalleryCarousel';
import ProductGrid from '@/components/storefront/ProductGrid';
import { FadeInSection } from '@/components/storefront/FadeInSection';
import StockBadge from '@/components/storefront/StockBadge';
import VariantSelector from '@/components/storefront/VariantSelector';
import UnitSelector from '@/components/storefront/UnitSelector';
import ShareButtons from '@/components/storefront/ShareButtons';
import { AddonGroupsSelector } from '@/components/storefront/AddonGroupsSelector';
import { WhatsAppAskButton } from '@/components/storefront/WhatsAppAskButton';
import type { CartAddon } from '@/lib/cart/addons';
import { isAddonSelectionValid } from '@/lib/cart/addonSelection';
import { useWishlistEnabled } from '@/lib/storefront/wishlist-enabled';
import { resolveMinOrderQty } from '@/lib/storefront/products/min-order-qty';
import { defaultProductUnit, maxWholeUnits, toUnitQty, unitMinBase, unitPrice, unitStepBase } from '@/lib/units/convert';
import { Button } from '@/components/storefront/ui/button';
import type { ProductForCard } from '@/components/storefront/ProductCard';
import type { VariantForCard } from '@/lib/types/variants';
import type { BreadcrumbDTO, ProductDetailDTO } from '@/lib/storefront/products/service';
import { matchTierForQty, resolveDisplayPricing } from '@/lib/storefront/products/tiers';
import { useCurrency } from '@/lib/currency/CurrencyContext';
import { formatPrice } from '@/lib/currency/formatPrice';
import { activeSaleFor, effectiveVariantPrice } from "@/lib/storefront/products/variantPrice";
import { shortProductUrl } from '@/lib/products/shortLink';
import { Price } from './Price';
import { useWishlist } from '@/lib/wishlist/WishlistContext';
import { useLocale } from 'next-intl';
import { trackCustomizeProduct, trackViewContent } from '@/lib/tracking/events';
type ProductDetailClientProps = {
  product: ProductDetailDTO;
  relatedProducts: ProductForCard[];
  locale: string;
  /** Store opt-in card for related products; also contains the gallery photos. */
  listingCard?: ListingCard;
  shareUrl?: string;
  breadcrumb?: BreadcrumbDTO;
  /**
   * Pre-selected variant id sourced from `?variant=<id>` on the PDP route.
   * When set, `selectedVariant` initializes to the matching variant so deep
   * links (marketing campaigns, shared chats) land on the right SKU.
   */
  initialVariantId?: string | null;
};

/**
 * Build the share URL for the currently-selected variant. `shareUrl` is the
 * bare product URL `${siteUrl}/${locale}/products/${slug}`. We append
 * `?variant=<sku>` (or `<id>` when no SKU). The bare URL stays canonical so
 * search engines treat all variant URLs as the same indexed page.
 */

function buildShareUrl(shareUrl: string, variant: VariantForCard | null): string {
  // Share the short form when the slug carries its 8-hex suffix, which is the
  // case for essentially every product. A percent-encoded Arabic slug runs the
  // canonical URL past 250 characters; /p/<code> is 29 and survives being
  // pasted into an Instagram story. /p/ redirects to the canonical page, so
  // the recipient still lands on the real URL.
  // Share the short form. A percent-encoded Arabic slug runs the canonical URL
  // past 250 characters — 87% of this catalogue's slugs are non-ASCII — which
  // is unusable in an Instagram story. /p/<code> is 29 characters and 307s to
  // the canonical page, verified in production.
  //
  // Falls through to the canonical URL when the slug carries no 8-hex code,
  // so an older or hand-written slug still shares a link that works.
  const base = shareUrl.split(/[?#]/)[0];
  const short = shortProductUrl(base);
  if (short) return short;
  if (!variant) return base;
  // Prefer the SKU and drop the param entirely when there is no readable one.
  // The old fallback was `variant.id`, a 36-character UUID that made the link
  // unshareable on Instagram while telling the recipient nothing. A share
  // without the param still lands on the product; the visitor just picks the
  // variant themselves, which is a far better outcome than a link nobody can
  // paste.
  const sku = variant.sku?.trim();
  if (!sku) return base;
  return `${base}?variant=${encodeURIComponent(sku)}`;
}
export default function ProductDetailClient(t0) {
  const $ = _c(129);
  const {
    product,
    relatedProducts,
    locale,
    shareUrl,
    breadcrumb,
    initialVariantId: t1,
    listingCard
  } = t0;
  const initialVariantId = t1 === undefined ? null : t1;
  const isRTL = locale === "ar";
  const {
    currency
  } = useCurrency();
  const router = useRouter();
  const pathname = usePathname();
  let t2;
  if ($[0] !== initialVariantId || $[1] !== product.variants) {
    t2 = initialVariantId ? product.variants.find(v => v.id === initialVariantId) ?? null : null;
    $[0] = initialVariantId;
    $[1] = product.variants;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const initialVariant = t2;
  const [selectedImage, setSelectedImage] = useState(initialVariant?.image_url ?? product.images[0]?.url ?? "");
  const [selectedVariant, setSelectedVariant] = useState(initialVariant);
  let t3;
  let t4;
  if ($[3] !== currency.code || $[4] !== currency.exchange_rate || $[5] !== product.id || $[6] !== product.name || $[7] !== product.price) {
    t3 = () => {
      trackViewContent({
        productId: product.id,
        name: product.name,
        price: product.price * currency.exchange_rate,
        currency: currency.code
      });
    };
    t4 = [product.id, product.name, product.price, currency.code, currency.exchange_rate];
    $[3] = currency.code;
    $[4] = currency.exchange_rate;
    $[5] = product.id;
    $[6] = product.name;
    $[7] = product.price;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  const isVariable = product.product_type === "variable";
  const isPackage = product.product_type === "package";
  const isSimple = product.product_type === "simple";
  let t5;
  if ($[10] !== pathname || $[11] !== product.color_image_map || $[12] !== product.color_option_key || $[13] !== product.id || $[14] !== product.name || $[15] !== router) {
    t5 = function handleVariantChange(variant) {
      setSelectedVariant(variant);
      const params = new URLSearchParams(window.location.search);
      if (variant) {
        params.set("variant", variant.sku ?? variant.id);
      } else {
        params.delete("variant");
      }
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, {
        scroll: false
      });
      if (!variant) {
        return;
      }
      trackCustomizeProduct(product.id, product.name);
      if (variant.image_url) {
        setSelectedImage(variant.image_url);
        return;
      }
      if (product.color_option_key) {
        const colorName = variant.options[product.color_option_key];
        if (colorName && product.color_image_map[colorName]) {
          setSelectedImage(product.color_image_map[colorName]);
        }
      }
    };
    $[10] = pathname;
    $[11] = product.color_image_map;
    $[12] = product.color_option_key;
    $[13] = product.id;
    $[14] = product.name;
    $[15] = router;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleVariantChange = t5;
  const wishlistEnabled = useWishlistEnabled();
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const [selectedAddons, setSelectedAddons] = useState(t6);
  const [addonErrorsShown, setAddonErrorsShown] = useState(false);
  let t7;
  if ($[18] !== product.addon_groups || $[19] !== selectedAddons) {
    const addonRules = product.addon_groups.map(_temp2);
    t7 = isAddonSelectionValid(addonRules, selectedAddons);
    $[18] = product.addon_groups;
    $[19] = selectedAddons;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const addonsInvalid = !t7;
  let t8;
  if ($[21] !== initialVariant?.min_order_qty || $[22] !== product.min_order_qty || $[23] !== product.units) {
    t8 = () => {
      const min = resolveMinOrderQty(product.min_order_qty, initialVariant?.min_order_qty ?? null);
      const defaultUnit = defaultProductUnit(product.units);
      return defaultUnit ? unitMinBase(min, defaultUnit.factor) : min;
    };
    $[21] = initialVariant?.min_order_qty;
    $[22] = product.min_order_qty;
    $[23] = product.units;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const [qtyForPricing, setQtyForPricing] = useState(t8);
  let t9;
  if ($[25] !== product.units) {
    t9 = () => defaultProductUnit(product.units)?.id ?? null;
    $[25] = product.units;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const [selectedUnitId, setSelectedUnitId] = useState(t9);
  const selectedUnit = product.units.find(u => u.id === selectedUnitId) ?? null;
  const factor = selectedUnit?.factor ?? 1;
  const effectiveMinQty = resolveMinOrderQty(product.min_order_qty, isVariable && selectedVariant ? selectedVariant.min_order_qty : null);
  let t10;
  if ($[27] !== effectiveMinQty) {
    t10 = () => {
      setQtyForPricing(q => q < effectiveMinQty ? effectiveMinQty : q);
    };
    $[27] = effectiveMinQty;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  useEffect(t10, [effectiveMinQty]);
  let t11;
  if ($[29] !== product.units) {
    t11 = function handleUnitChange(nextUnitId) {
      if (!product.units.some(u_0 => u_0.id === nextUnitId)) {
        return;
      }
      setSelectedUnitId(nextUnitId);
    };
    $[29] = product.units;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const handleUnitChange = t11;
  let activeSale;
  let t12;
  let variantPrice;
  if ($[31] !== isVariable || $[32] !== product.has_price_tiers || $[33] !== product.in_price_group || $[34] !== product.price || $[35] !== product.price_tiers || $[36] !== product.sale || $[37] !== qtyForPricing || $[38] !== selectedVariant) {
    variantPrice = isVariable && selectedVariant ? effectiveVariantPrice(selectedVariant.price, product.price) : product.price;
    let t13;
    if ($[42] !== isVariable || $[43] !== product.has_price_tiers || $[44] !== product.price_tiers || $[45] !== qtyForPricing || $[46] !== selectedVariant) {
      t13 = product.has_price_tiers && product.price_tiers?.length ? matchTierForQty(product.price_tiers, qtyForPricing, isVariable && selectedVariant ? selectedVariant.id : null) : null;
      $[42] = isVariable;
      $[43] = product.has_price_tiers;
      $[44] = product.price_tiers;
      $[45] = qtyForPricing;
      $[46] = selectedVariant;
      $[47] = t13;
    } else {
      t13 = $[47];
    }
    const matchedTier = t13;
    const tierAppliedPrice = matchedTier?.unit_price ?? variantPrice;
    const tierMatched = matchedTier !== null;
    activeSale = activeSaleFor({
      isVariable,
      variantSale: selectedVariant?.sale,
      productSale: product.sale,
      price: variantPrice
    });
    t12 = resolveDisplayPricing({
      tierPrice: tierAppliedPrice,
      tierMatched,
      sale: activeSale,
      untieredPrice: product.in_price_group ? variantPrice : undefined
    });
    $[31] = isVariable;
    $[32] = product.has_price_tiers;
    $[33] = product.in_price_group;
    $[34] = product.price;
    $[35] = product.price_tiers;
    $[36] = product.sale;
    $[37] = qtyForPricing;
    $[38] = selectedVariant;
    $[39] = activeSale;
    $[40] = t12;
    $[41] = variantPrice;
  } else {
    activeSale = $[39];
    t12 = $[40];
    variantPrice = $[41];
  }
  const {
    displayPrice,
    compareAtPrice
  } = t12;
  const displayStock = isVariable && selectedVariant ? selectedVariant.stock : product.stock;
  const isTrackingStock = isVariable ? selectedVariant?.stock !== null && selectedVariant?.stock !== undefined : product.track_stock;
  const isOutOfStock = isTrackingStock && displayStock !== null && displayStock !== undefined && displayStock <= 0;
  const needsVariantSelection = isVariable && !selectedVariant;
  const addToCartDisabled = isOutOfStock || needsVariantSelection;
  const selectedUnitLabel = selectedUnit ? isRTL ? selectedUnit.name_ar : selectedUnit.name_en ?? selectedUnit.name_ar : null;
  const availableWholeUnits = selectedUnit ? maxWholeUnits(displayStock, factor) : null;
  const t13 = listingCard === "glass-spec";
  let t14;
  if ($[48] !== locale || $[49] !== product.images || $[50] !== product.name || $[51] !== selectedImage || $[52] !== t13) {
    t14 = <ProductGalleryCarousel contain={t13} images={product.images} selectedImage={selectedImage} onSelect={setSelectedImage} productName={product.name} locale={locale} />;
    $[48] = locale;
    $[49] = product.images;
    $[50] = product.name;
    $[51] = selectedImage;
    $[52] = t13;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  if ($[54] !== breadcrumb || $[55] !== locale || $[56] !== product.name) {
    t15 = <Breadcrumb breadcrumb={breadcrumb} productName={product.name} locale={locale} />;
    $[54] = breadcrumb;
    $[55] = locale;
    $[56] = product.name;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== product.name) {
    t16 = <h1 className="text-2xl md:text-3xl font-semibold text-foreground leading-tight">{product.name}</h1>;
    $[58] = product.name;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== isPackage || $[61] !== isSimple || $[62] !== product.stock || $[63] !== product.track_stock) {
    t17 = (isSimple || isPackage) && product.track_stock && <StockBadge stock={product.stock} trackStock={product.track_stock} />;
    $[60] = isPackage;
    $[61] = isSimple;
    $[62] = product.stock;
    $[63] = product.track_stock;
    $[64] = t17;
  } else {
    t17 = $[64];
  }
  const t18 = product.has_price_tiers && (product.price_tiers?.length ?? 0) > 0;
  let t19;
  if ($[65] !== compareAtPrice || $[66] !== displayPrice || $[67] !== isVariable || $[68] !== locale || $[69] !== product.price || $[70] !== product.variants || $[71] !== qtyForPricing || $[72] !== selectedVariant || $[73] !== t18) {
    t19 = <PriceBlock displayPrice={displayPrice} compareAtPrice={compareAtPrice} productPrice={product.price} isVariable={isVariable} selectedVariant={selectedVariant} variants={product.variants} locale={locale} quantity={qtyForPricing} showTotal={t18} />;
    $[65] = compareAtPrice;
    $[66] = displayPrice;
    $[67] = isVariable;
    $[68] = locale;
    $[69] = product.price;
    $[70] = product.variants;
    $[71] = qtyForPricing;
    $[72] = selectedVariant;
    $[73] = t18;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  let t20;
  if ($[75] !== isVariable || $[76] !== selectedVariant) {
    t20 = isVariable && selectedVariant && <StockBadge stock={selectedVariant.stock} />;
    $[75] = isVariable;
    $[76] = selectedVariant;
    $[77] = t20;
  } else {
    t20 = $[77];
  }
  let t21;
  if ($[78] !== product.description_html) {
    t21 = product.description_html && <div className="prose prose-sm max-w-none text-sm text-muted-foreground leading-relaxed [&_a]:text-primary [&_a]:underline" dangerouslySetInnerHTML={{
      __html: product.description_html
    }} />;
    $[78] = product.description_html;
    $[79] = t21;
  } else {
    t21 = $[79];
  }
  let t22;
  if ($[80] !== product.note) {
    t22 = product.note && <div className="flex gap-2.5 rounded-xl border border-primary/20 bg-primary/5 px-3.5 py-3"><Info size={18} weight="fill" className="mt-0.5 shrink-0 text-primary" aria-hidden="true" /><p className="text-sm leading-relaxed text-foreground whitespace-pre-line">{product.note}</p></div>;
    $[80] = product.note;
    $[81] = t22;
  } else {
    t22 = $[81];
  }
  let t23;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="border-t border-border" />;
    $[82] = t23;
  } else {
    t23 = $[82];
  }
  let t24;
  if ($[83] !== handleVariantChange || $[84] !== isVariable || $[85] !== product.color_hex_map || $[86] !== product.option_names || $[87] !== product.variants) {
    t24 = isVariable && product.option_names.length > 0 && <VariantSelector optionNames={product.option_names} variants={product.variants} colorHexMap={product.color_hex_map} onVariantChange={handleVariantChange} onColorPreview={imageUrl => {
      if (imageUrl) {
        setSelectedImage(imageUrl);
      }
    }} />;
    $[83] = handleVariantChange;
    $[84] = isVariable;
    $[85] = product.color_hex_map;
    $[86] = product.option_names;
    $[87] = product.variants;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  let t25;
  if ($[89] !== handleUnitChange || $[90] !== isRTL || $[91] !== product.units || $[92] !== selectedUnitId) {
    t25 = product.units.length > 0 && selectedUnitId && <UnitSelector units={product.units} selectedId={selectedUnitId} onChange={handleUnitChange} isRTL={isRTL} />;
    $[89] = handleUnitChange;
    $[90] = isRTL;
    $[91] = product.units;
    $[92] = selectedUnitId;
    $[93] = t25;
  } else {
    t25 = $[93];
  }
  let t26;
  if ($[94] !== isPackage || $[95] !== isRTL || $[96] !== product.package_items || $[97] !== product.package_price_mode) {
    t26 = isPackage && product.package_items.length > 0 && <PackageContents items={product.package_items} priceMode={product.package_price_mode} isRTL={isRTL} />;
    $[94] = isPackage;
    $[95] = isRTL;
    $[96] = product.package_items;
    $[97] = product.package_price_mode;
    $[98] = t26;
  } else {
    t26 = $[98];
  }
  let t27;
  if ($[99] !== addonErrorsShown || $[100] !== isRTL || $[101] !== product.addon_groups || $[102] !== selectedAddons) {
    t27 = product.addon_groups.length > 0 && <AddonGroupsSelector groups={product.addon_groups} selected={selectedAddons} onChange={setSelectedAddons} isRTL={isRTL} showErrors={addonErrorsShown} />;
    $[99] = addonErrorsShown;
    $[100] = isRTL;
    $[101] = product.addon_groups;
    $[102] = selectedAddons;
    $[103] = t27;
  } else {
    t27 = $[103];
  }
  let t28;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setAddonErrorsShown(true);
    $[104] = t28;
  } else {
    t28 = $[104];
  }
  let t29;
  if ($[105] !== product.id || $[106] !== wishlistEnabled) {
    t29 = wishlistEnabled ? <WishlistAction productId={product.id} /> : undefined;
    $[105] = product.id;
    $[106] = wishlistEnabled;
    $[107] = t29;
  } else {
    t29 = $[107];
  }
  let t30;
  if ($[108] !== selectedVariant || $[109] !== shareUrl) {
    t30 = shareUrl ? buildShareUrl(shareUrl, selectedVariant) : null;
    $[108] = selectedVariant;
    $[109] = shareUrl;
    $[110] = t30;
  } else {
    t30 = $[110];
  }
  let t31;
  if ($[111] !== product.name || $[112] !== t30) {
    t31 = <WhatsAppAskButton productName={product.name} productUrl={t30} />;
    $[111] = product.name;
    $[112] = t30;
    $[113] = t31;
  } else {
    t31 = $[113];
  }
  let t32;
  if ($[114] !== locale || $[115] !== product.description || $[116] !== product.images || $[117] !== product.name || $[118] !== selectedVariant || $[119] !== shareUrl) {
    t32 = shareUrl && <div className="pt-4 border-t border-border/40"><ShareButtons url={buildShareUrl(shareUrl, selectedVariant)} title={product.name} description={product.description ?? undefined} imageUrl={selectedVariant?.image_url ?? product.images[0]?.url} locale={locale} /></div>;
    $[114] = locale;
    $[115] = product.description;
    $[116] = product.images;
    $[117] = product.name;
    $[118] = selectedVariant;
    $[119] = shareUrl;
    $[120] = t32;
  } else {
    t32 = $[120];
  }
  let t33;
  if ($[121] !== isRTL || $[122] !== product.specs) {
    t33 = product.specs.length > 0 && <FadeInSection className="mt-12"><h2 className="text-xl font-semibold text-foreground mb-4">{isRTL ? "\u0627\u0644\u0645\u0648\u0627\u0635\u0641\u0627\u062A" : "Specifications"}</h2><div className="rounded-xl border border-border overflow-hidden">{product.specs.map((spec, i_0) => <div key={i_0} data-spec-row="" className={["grid grid-cols-[2fr_3fr] text-sm", i_0 % 2 === 0 ? "bg-subtle/50" : "", i_0 < product.specs.length - 1 ? "border-b border-border" : ""].join(" ")}><span className="px-4 py-3 font-medium text-foreground">{isRTL ? spec.key_ar : spec.key_en}</span><span className="px-4 py-3 text-muted-foreground">{isRTL ? spec.value_ar : spec.value_en}</span></div>)}</div></FadeInSection>;
    $[121] = isRTL;
    $[122] = product.specs;
    $[123] = t33;
  } else {
    t33 = $[123];
  }
  let t34;
  if ($[124] !== isRTL || $[125] !== listingCard || $[126] !== locale || $[127] !== relatedProducts) {
    t34 = relatedProducts.length > 0 && <FadeInSection className="mt-12"><h2 className="text-xl font-semibold text-foreground mb-6">{isRTL ? "\u0645\u0646\u062A\u062C\u0627\u062A \u0630\u0627\u062A \u0635\u0644\u0629" : "Related Products"}</h2><ProductGrid products={relatedProducts} locale={locale} listingCard={listingCard} /></FadeInSection>;
    $[124] = isRTL;
    $[125] = listingCard;
    $[126] = locale;
    $[127] = relatedProducts;
    $[128] = t34;
  } else {
    t34 = $[128];
  }
  return <div data-page-shell="" className="max-w-[1536px] mx-auto px-4 py-6 md:py-10"><div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">{t14}<div className="flex flex-col gap-5">{t15}{t16}{t17}{t19}{selectedUnit && product.base_unit && <div className="flex flex-col gap-0.5"><div className="text-2xl font-bold text-foreground">{formatPrice(unitPrice(displayPrice, factor), currency)}{" / "}{selectedUnitLabel}</div><div className="text-sm text-muted-foreground">{formatPrice(displayPrice, currency)}{" / "}{isRTL ? product.base_unit.name_ar : product.base_unit.name_en ?? product.base_unit.name_ar}</div></div>}{t20}{t21}{t22}{t23}{t24}{t25}{t26}{t27}<AddToCartButton productId={product.id} minQty={unitMinBase(effectiveMinQty, factor)} qtyStep={unitStepBase(factor, product.qty_step ?? 1)} price={variantPrice} variant="full" variantId={selectedVariant?.id} disabled={addToCartDisabled} needsSelection={needsVariantSelection} stock={isTrackingStock ? displayStock : null} hasPriceTiers={product.has_price_tiers} priceTiers={product.price_tiers} inPriceGroup={product.in_price_group} sale={activeSale} onQtyChange={setQtyForPricing} addons={selectedAddons} addonsInvalid={addonsInvalid} onInvalidAddons={t28} productUnitId={selectedUnitId} unitFactor={factor} unitLabel={selectedUnitLabel} extraAction={t29} />{selectedUnit && isTrackingStock && <span className="text-sm text-muted-foreground">{toUnitQty(qtyForPricing, factor)} {selectedUnitLabel}{availableWholeUnits !== null && Number.isFinite(availableWholeUnits) && <>{" \xB7 "}{isRTL ? `${availableWholeUnits} ${selectedUnitLabel} متوفرة` : `${availableWholeUnits} ${selectedUnitLabel} in stock`}</>}</span>}{t31}{t32}</div></div>{t33}{t34}</div>;
}

// ─── Sub-components ────────────────────────────────────────────────
function _temp2(g) {
  return {
    id: g.id,
    is_required: g.is_required,
    min_select: g.min_select,
    max_select: g.max_select,
    itemIds: g.items.map(_temp)
  };
}
function _temp(i) {
  return i.id;
}
function Breadcrumb(t0) {
  const $ = _c(16);
  const {
    breadcrumb,
    productName,
    locale
  } = t0;
  const isRTL = locale === "ar";
  const Caret = isRTL ? CaretLeft : CaretRight;
  const t1 = isRTL ? "\u0645\u0633\u0627\u0631 \u0627\u0644\u062A\u0646\u0642\u0644" : "Breadcrumb";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <House size={12} weight="regular" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = isRTL ? "\u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629" : "Home";
  let t4;
  if ($[1] !== t3) {
    t4 = <Link href="/" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">{t2}<span>{t3}</span></Link>;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== Caret || $[4] !== breadcrumb) {
    t5 = breadcrumb && <><Caret size={12} className="text-muted-foreground/60 shrink-0" /><Link href={`/categories/${breadcrumb.category_slug}`} className="hover:text-foreground transition-colors">{breadcrumb.category_name}</Link></>;
    $[3] = Caret;
    $[4] = breadcrumb;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== Caret) {
    t6 = <Caret size={12} className="text-muted-foreground/60 shrink-0" />;
    $[6] = Caret;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== productName) {
    t7 = <span data-pdp-crumb="" className="text-primary font-medium truncate max-w-[200px]"><bdi dir="auto">{productName}</bdi></span>;
    $[8] = productName;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t1 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = <nav aria-label={t1} className="flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap">{t4}{t5}{t6}{t7}</nav>;
    $[10] = t1;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
export function PriceBlock(t0) {
  const $ = _c(68);
  const {
    displayPrice,
    compareAtPrice,
    isVariable,
    selectedVariant,
    variants,
    productPrice,
    locale,
    quantity: t1,
    showTotal: t2
  } = t0;
  const quantity = t1 === undefined ? 1 : t1;
  const showTotal = t2 === undefined ? false : t2;
  if (isVariable && !selectedVariant) {
    let first;
    let max;
    let min;
    let t3;
    let t4;
    if ($[0] !== productPrice || $[1] !== variants) {
      t4 = Symbol.for("react.early_return_sentinel");
      bb0: {
        const active = variants.filter(_temp3);
        if (active.length === 0) {
          t4 = null;
          break bb0;
        }
        let t5;
        if ($[7] !== productPrice) {
          t5 = v_0 => {
            const list = effectiveVariantPrice(v_0.price, productPrice);
            const sale = v_0.sale;
            return sale && sale.sale_price > 0 && sale.sale_price < sale.original_price ? {
              current: sale.sale_price,
              original: sale.original_price
            } : {
              current: list,
              original: list
            };
          };
          $[7] = productPrice;
          $[8] = t5;
        } else {
          t5 = $[8];
        }
        const offers = active.map(t5);
        const currents = offers.map(_temp4);
        min = Math.min(...currents);
        max = Math.max(...currents);
        [first] = offers;
        t3 = max === min && first.current < first.original && offers.every(o_0 => o_0.original === first.original);
      }
      $[0] = productPrice;
      $[1] = variants;
      $[2] = first;
      $[3] = max;
      $[4] = min;
      $[5] = t3;
      $[6] = t4;
    } else {
      first = $[2];
      max = $[3];
      min = $[4];
      t3 = $[5];
      t4 = $[6];
    }
    if (t4 !== Symbol.for("react.early_return_sentinel")) {
      return t4;
    }
    if (t3) {
      let t5;
      if ($[9] !== first.current || $[10] !== first.original) {
        t5 = {
          current: first.current,
          original: first.original
        };
        $[9] = first.current;
        $[10] = first.original;
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      let t6;
      if ($[12] !== first.original || $[13] !== t5) {
        t6 = {
          base: first.original,
          sale: t5
        };
        $[12] = first.original;
        $[13] = t5;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      let t7;
      if ($[15] !== locale || $[16] !== t6) {
        t7 = <Price pricing={t6} locale={locale} size="lg" />;
        $[15] = locale;
        $[16] = t6;
        $[17] = t7;
      } else {
        t7 = $[17];
      }
      return t7;
    }
    let t5;
    if ($[18] !== max || $[19] !== min) {
      t5 = max > min ? {
        min,
        max
      } : null;
      $[18] = max;
      $[19] = min;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    let t6;
    if ($[21] !== min || $[22] !== t5) {
      t6 = {
        base: min,
        range: t5
      };
      $[21] = min;
      $[22] = t5;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    let t7;
    if ($[24] !== locale || $[25] !== t6) {
      t7 = <Price pricing={t6} locale={locale} size="lg" />;
      $[24] = locale;
      $[25] = t6;
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    return t7;
  }
  if (showTotal) {
    const total = displayPrice * quantity;
    const isRTL = locale === "ar";
    let t3;
    if ($[27] !== compareAtPrice || $[28] !== displayPrice) {
      t3 = compareAtPrice ? {
        current: displayPrice,
        original: compareAtPrice
      } : null;
      $[27] = compareAtPrice;
      $[28] = displayPrice;
      $[29] = t3;
    } else {
      t3 = $[29];
    }
    const unitSale = t3;
    let t4;
    if ($[30] !== compareAtPrice || $[31] !== quantity || $[32] !== total) {
      t4 = compareAtPrice ? {
        current: total,
        original: compareAtPrice * quantity
      } : null;
      $[30] = compareAtPrice;
      $[31] = quantity;
      $[32] = total;
      $[33] = t4;
    } else {
      t4 = $[33];
    }
    const totalSale = t4;
    let t5;
    if ($[34] !== displayPrice || $[35] !== unitSale) {
      t5 = {
        base: displayPrice,
        sale: unitSale
      };
      $[34] = displayPrice;
      $[35] = unitSale;
      $[36] = t5;
    } else {
      t5 = $[36];
    }
    let t6;
    if ($[37] !== locale || $[38] !== t5) {
      t6 = <Price pricing={t5} locale={locale} size="lg" />;
      $[37] = locale;
      $[38] = t5;
      $[39] = t6;
    } else {
      t6 = $[39];
    }
    const t7 = isRTL ? "/ \u0627\u0644\u0642\u0637\u0639\u0629" : "/ unit";
    let t8;
    if ($[40] !== t7) {
      t8 = <span className="text-sm text-muted-foreground">{t7}</span>;
      $[40] = t7;
      $[41] = t8;
    } else {
      t8 = $[41];
    }
    let t9;
    if ($[42] !== t6 || $[43] !== t8) {
      t9 = <div className="flex items-baseline gap-1.5 flex-wrap">{t6}{t8}</div>;
      $[42] = t6;
      $[43] = t8;
      $[44] = t9;
    } else {
      t9 = $[44];
    }
    const t10 = isRTL ? `الإجمالي لـ ${quantity} قطعة:` : `Total for ${quantity} units:`;
    let t11;
    if ($[45] !== t10) {
      t11 = <span>{t10}</span>;
      $[45] = t10;
      $[46] = t11;
    } else {
      t11 = $[46];
    }
    let t12;
    if ($[47] !== total || $[48] !== totalSale) {
      t12 = {
        base: total,
        sale: totalSale
      };
      $[47] = total;
      $[48] = totalSale;
      $[49] = t12;
    } else {
      t12 = $[49];
    }
    let t13;
    if ($[50] !== locale || $[51] !== t12) {
      t13 = <Price pricing={t12} locale={locale} size="sm" showPercentBadge={false} />;
      $[50] = locale;
      $[51] = t12;
      $[52] = t13;
    } else {
      t13 = $[52];
    }
    let t14;
    if ($[53] !== t11 || $[54] !== t13) {
      t14 = <p className="text-xs text-muted-foreground flex items-baseline gap-1 flex-wrap">{t11}{t13}</p>;
      $[53] = t11;
      $[54] = t13;
      $[55] = t14;
    } else {
      t14 = $[55];
    }
    let t15;
    if ($[56] !== t14 || $[57] !== t9) {
      t15 = <div className="flex flex-col gap-1">{t9}{t14}</div>;
      $[56] = t14;
      $[57] = t9;
      $[58] = t15;
    } else {
      t15 = $[58];
    }
    return t15;
  }
  let t3;
  if ($[59] !== compareAtPrice || $[60] !== displayPrice) {
    t3 = compareAtPrice ? {
      current: displayPrice,
      original: compareAtPrice
    } : null;
    $[59] = compareAtPrice;
    $[60] = displayPrice;
    $[61] = t3;
  } else {
    t3 = $[61];
  }
  let t4;
  if ($[62] !== displayPrice || $[63] !== t3) {
    t4 = {
      base: displayPrice,
      sale: t3
    };
    $[62] = displayPrice;
    $[63] = t3;
    $[64] = t4;
  } else {
    t4 = $[64];
  }
  let t5;
  if ($[65] !== locale || $[66] !== t4) {
    t5 = <Price pricing={t4} locale={locale} size="lg" />;
    $[65] = locale;
    $[66] = t4;
    $[67] = t5;
  } else {
    t5 = $[67];
  }
  return t5;
}
function _temp4(o) {
  return o.current;
}
function _temp3(v) {
  return v.is_active;
}
function PackageContents(t0) {
  const $ = _c(19);
  const {
    items,
    priceMode,
    isRTL
  } = t0;
  const {
    currency
  } = useCurrency();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Package size={16} className="text-muted-foreground" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = isRTL ? "\u0645\u062D\u062A\u0648\u064A\u0627\u062A \u0627\u0644\u0628\u0627\u0642\u0629" : "Package includes";
  let t3;
  if ($[1] !== t2) {
    t3 = <div className="flex items-center gap-2 mb-3">{t1}<p className="text-sm font-semibold text-foreground">{t2}</p></div>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== currency || $[4] !== isRTL || $[5] !== items) {
    let t5;
    if ($[7] !== currency || $[8] !== isRTL) {
      t5 = (item, i) => <li key={i} className="flex justify-between text-sm"><span className="text-muted-foreground">{item.quantity}× {isRTL ? item.name_ar : item.name_en}</span><span className="text-muted-foreground tabular-nums">{formatPrice(item.price * item.quantity, currency)}</span></li>;
      $[7] = currency;
      $[8] = isRTL;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = items.map(t5);
    $[3] = currency;
    $[4] = isRTL;
    $[5] = items;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <ul className="space-y-1.5">{t4}</ul>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== isRTL || $[13] !== priceMode) {
    t6 = priceMode === "sum" && <p className="text-xs text-muted-foreground mt-3">{isRTL ? "\u0627\u0644\u0633\u0639\u0631 = \u0645\u062C\u0645\u0648\u0639 \u0623\u0633\u0639\u0627\u0631 \u0627\u0644\u0645\u0646\u062A\u062C\u0627\u062A" : "Price = sum of included products"}</p>;
    $[12] = isRTL;
    $[13] = priceMode;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t3 || $[16] !== t5 || $[17] !== t6) {
    t7 = <div className="rounded-xl border border-border p-4">{t3}{t5}{t6}</div>;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
function WishlistAction({
  productId
}: {
  productId: string;
}) {
  const {
    isInWishlist,
    toggle,
    isAuthenticated
  } = useWishlist();
  const router = useRouter();
  const locale = useLocale();
  const isRTL = locale === 'ar';
  const inWishlist = isInWishlist(productId);
  const [loading, setLoading] = useState(false);
  async function handleClick() {
    if (!isAuthenticated) {
      router.push(`/${locale}/login?redirect=/wishlist`);
      return;
    }
    setLoading(true);
    try {
      await toggle(productId);
    } finally {
      setLoading(false);
    }
  }
  return <Button type="button" onClick={handleClick} color="primary" variant="soft" size="lg" fullWidth loading={loading} leftIcon={<Heart weight={inWishlist ? 'fill' : 'regular'} />}>
      {inWishlist ? isRTL ? 'في المفضلة' : 'In Wishlist' : isRTL ? 'المفضلة' : 'Wishlist'}
    </Button>;
}